// A setInterval that respects the tab's visibility.
//
// Background tabs kept every page poller running at full rate (shell flags,
// error logs, sync status …), so a user with five forgotten EventHub tabs
// multiplied the polling load five times for screens nobody looked at.
// This poller:
//   - skips a tick while `document.hidden` is true,
//   - runs once when the tab becomes visible again, but only if a tick was
//     missed (the last run is older than the interval),
//   - never starts a run while the previous one is still in flight.

interface VisibilityDoc {
  readonly hidden: boolean
  addEventListener(type: 'visibilitychange', listener: () => void): void
  removeEventListener(type: 'visibilitychange', listener: () => void): void
}

export interface VisibilityPollerOptions {
  /** Run once right away on start() (default true). */
  immediate?: boolean
  /** Injected for tests; defaults to the global document. */
  doc?: VisibilityDoc
}

export interface VisibilityPoller {
  start(): void
  stop(): void
  isRunning(): boolean
}

export function createVisibilityPoller(
  fn: () => unknown,
  intervalMs: number,
  options: VisibilityPollerOptions = {},
): VisibilityPoller {
  const immediate = options.immediate ?? true
  const getDoc = (): VisibilityDoc | undefined =>
    options.doc ?? (typeof document !== 'undefined' ? document : undefined)

  let handle: ReturnType<typeof setInterval> | null = null
  let inFlight = false
  let lastRunAt = 0

  const isHidden = () => getDoc()?.hidden === true

  const run = () => {
    if (inFlight) return
    inFlight = true
    lastRunAt = Date.now()
    let result: unknown
    try {
      result = fn()
    } catch {
      inFlight = false
      return
    }
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      ;(result as Promise<unknown>).then(
        () => { inFlight = false },
        () => { inFlight = false },
      )
    } else {
      inFlight = false
    }
  }

  const tick = () => {
    if (isHidden()) return
    run()
  }

  const onVisibility = () => {
    if (isHidden()) return
    if (Date.now() - lastRunAt >= intervalMs) run()
  }

  return {
    start() {
      if (handle) return
      if (immediate && !isHidden()) run()
      handle = setInterval(tick, intervalMs)
      getDoc()?.addEventListener('visibilitychange', onVisibility)
    },
    stop() {
      if (handle) clearInterval(handle)
      handle = null
      getDoc()?.removeEventListener('visibilitychange', onVisibility)
    },
    isRunning: () => handle !== null,
  }
}
